'use client';

import { useState } from 'react';
import { signInWithPhoneNumber, ConfirmationResult, getAuth } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import posthog from 'posthog-js';
import { captureClientException } from '@instamenu/posthog/client';
import { recaptchaVerifier } from './recaptcha';
declare const grecaptcha: {
  getResponse(widgetId: number): unknown;
  reset: (widgetId: number) => void;
};

const expectedPhoneAuthErrorCodes = new Set([
  'auth/invalid-verification-code',
  'auth/code-expired',
  'auth/invalid-phone-number',
]);

function reportPhoneAuthError(error: unknown, source: string) {
  const firebaseErrorCode = error instanceof FirebaseError ? error.code : undefined;

  if (firebaseErrorCode && expectedPhoneAuthErrorCodes.has(firebaseErrorCode)) {
    posthog.capture('phone_auth_failed', {
      source,
      firebase_error_code: firebaseErrorCode,
    });
    return;
  }

  captureClientException(error, {
    source,
    properties: { firebase_error_code: firebaseErrorCode },
  });
}

export const usePhoneSignIn = () => {
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const sendCodeToPhone = async (phoneNumber: string) => {
    setLoading(true);
    setError(undefined);

    return signInWithPhoneNumber(getAuth(), phoneNumber, recaptchaVerifier)
      .then((result) => {
        setConfirmationResult(result);
        return result;
      })
      .catch((e) => {
        reportPhoneAuthError(e, 'firebase.phone_auth.send_code');
        recaptchaVerifier.render().then((widgetId) => {
          grecaptcha.reset(widgetId);
        });
        setError('An error occurred: Please try again.');
      })
      .finally(() => setLoading(false));
  };

  const confirmCode = async (code: string) => {
    if (!confirmationResult) return;
    setLoading(true);
    setError(undefined);
    return confirmationResult

      .confirm(code)
      .catch((e) => {
        reportPhoneAuthError(e, 'firebase.phone_auth.confirm_code');
        setError('That code is invalid. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  return { sendCodeToPhone, confirmCode, loading, error };
};
